import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ShieldQuestion } from 'lucide-react';
import Layout from './components/Layout';
import RequirePage from './components/RequirePage';
import TicketBoard from './pages/TicketBoard';
import TicketDetail from './pages/TicketDetail';
import ReviewBoard from './pages/ReviewBoard';
import ApprovalQueue from './pages/ApprovalQueue';
import Authorizations from './pages/Authorizations';
import Masters from './pages/Masters';
import AuditTrail from './pages/AuditTrail';
import Settings from './pages/Settings';
import { useAuth } from './contexts/AuthContext';

import { Button } from './components/ui/Button';
import { AppShellSkeleton } from './components/Layout';

import { WhatsNewProvider, WhatsNewPanel, WhatsNewDialog, entries } from './whats-new';
import { DRA_TONES } from './lib/whats-new-tones';
import { trackPageView } from './services/usage';

/*
 * ── WHAT'S NEW (P41) ────────────────────────────────────────────────────────────────────────
 * A copied folder (`web/src/whats-new/` + `api/src/functions/whats-new/`), taken from
 * `customer360-app` at `a538226`. `context/docs/whats-new.md` §1 is what a later reader diffs
 * against and lists every adaptation; the two the module's own README predicts are here:
 *
 * **`apiBase` has no version prefix.** The module's routes are `whats-new/state`, `…/seen` and
 * `…/image/{id}`, deliberately NOT under `v1/`, because the folder carries its own paths into
 * whichever app copies it. House rule 4 says `/api/v1/<resource>` with the `{success, data, meta}`
 * envelope — the ENVELOPE is honoured (`get-state.ts` uses `shared/response.ts`), the prefix is
 * not. Recorded as a deviation in `design.md` §17 rather than resolved: renaming the routes would
 * fork the folder on its first day, and the alternative — three endpoints outside `v1/` — is
 * visible in exactly one place, which is this line.
 *
 * **`getToken` returns an empty string, and that is the whole adaptation.** The module was built
 * against MSAL bearer tokens; the DRA runs on SWA EasyAuth, where the identity is a cookie the
 * gateway turns into `x-ms-client-principal`. Its four `fetch` calls are same-origin and relative,
 * so the cookie rides along by itself and the `Authorization: Bearer ` header they also send is
 * ignored by the host. Nothing inside the folder needed changing for it — which is the seam
 * working — but the seam does ASSUME a bearer token, and that is worth saying out loud: a host
 * with no token has to pass a function that answers nothing.
 *
 * `tones` is not optional here. The defaults hold in a stock Tailwind app and the DRA is one
 * (P35 extends the palette rather than replacing it), so they would RENDER — in stock green and
 * stock blue, next to a screen built from measured tokens. `lib/whats-new-tones.ts` says why
 * that is the worse failure.
 *
 * No `onEvent`: the module works silently without it, and the DRA's one ledger
 * (`Dra.AuditTrail`, invariant 5) is about who did what to a TICKET. "Somebody opened a panel"
 * is not that, and a second series is a table nobody asked for. Named in
 * `context/docs/whats-new.md` §4 as open rather than decided against for ever.
 * ───────────────────────────────────────────────────────────────────────────────────────── */
const WHATS_NEW_API_BASE = '/api';
const noBearerToken = async () => '';

/*
 * Usage telemetry (2026-10-08): one `page_view` per route change, for
 * `reporting.dolderapps.com/app-usage`. Mounted only in the branch below where the reader HAS
 * access — the ingest endpoint refuses everybody else (`ANY_DRA_USER`), so mounting it on the
 * "No access yet" screen would only queue posts that are bound to answer 403.
 */
function UsageTracker() {
  const { pathname } = useLocation();
  useEffect(() => { trackPageView(pathname); }, [pathname]);
  return null;
}

export default function App() {
  const { user, loading, login, logout } = useAuth();

  /*
   * THE FIRST PAINT (P38, task 5 — audit S2-19)
   *
   * *"Measured after the SSO return: the entire document is the word `Loading…`
   * — no sidebar, no header, no skeleton."*
   *
   * The sidebar's NAV cannot be drawn yet and that is not a detail to paper over:
   * which entries a person gets depends on their role, so guessing would show a
   * Viewer links they do not have and then take them away. What CAN be drawn is
   * everything that is true for every reader — the Dolder lockup, the app name,
   * the rail, the header band, the content frame — and grey bars where the nav
   * rows will be.
   *
   * So the shell arrives at once and only the parts that depend on WHO is reading
   * wait, which is the honest division. `AppShellSkeleton` lives in `Layout.tsx`
   * next to the real shell for the reason `SkeletonTable` lives beside the real
   * table: a skeleton in a second file is a second layout, and the reader sees it
   * shift when the two drift.
   */
  if (loading) return <AppShellSkeleton />;

  if (!user) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-800">DRA — Digital Regulatory Assistant</h1>
        <p className="text-gray-500">Sign in with your Dolder account to continue.</p>
        <Button variant="primary" onClick={login} className="px-6">Sign in</Button>
      </div>
    );
  }

  // A signed-in Entra user with no role at all. This is what EVERY new
  // colleague sees on their first visit: EasyAuth lets them in (they have a
  // Dolder account), `[Dra].UserRoles` has no row for them, so every page grant
  // resolves to nothing. Without this branch they got the full shell with an
  // empty sidebar and a blank panel — a screen that looks broken and gives them
  // nothing to act on. Roman's onboarding point.
  if (user.accessiblePages.length === 0) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 px-6 text-center">
        <ShieldQuestion className="h-10 w-10 text-muted" />
        <h1 className="text-2xl font-bold text-ink">No access yet</h1>
        <p className="max-w-md text-sm text-gray-600">
          You are signed in as <span className="font-medium text-gray-900">{user.email}</span>, but no role has been
          assigned to this account yet. Please contact an administrator — they can grant you access under Settings.
        </p>
        <p className="max-w-md text-xs text-muted">
          Once a role has been assigned, reload this page to pick it up.
        </p>
        <button
          onClick={logout}
          className="mt-2 rounded-lg border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <WhatsNewProvider
      getToken={noBearerToken}
      apiBase={WHATS_NEW_API_BASE}
      entries={entries}
      tones={DRA_TONES}
    >
      {/* Rendered once, outside the routes: both are overlays and neither belongs to a page.
          The blocking dialog draws only for an unseen `major` entry, and there is none. */}
      <WhatsNewDialog />
      <WhatsNewPanel />
      <UsageTracker />
      <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/tickets" replace />} />
        <Route path="/tickets" element={<RequirePage page="/tickets"><TicketBoard /></RequirePage>} />
        <Route path="/tickets/:ticketNumber" element={<RequirePage page="/tickets"><TicketDetail /></RequirePage>} />
        {/* The review cockpit (P22) is a FILTERED VIEW of the ticket board, not
            a page of its own: it is gated on `/tickets`, so it needs no
            migration and no permission-matrix row. Whoever can see tickets can
            see the ones that stopped. */}
        <Route path="/review" element={<RequirePage page="/tickets"><ReviewBoard /></RequirePage>} />
        <Route path="/approvals" element={<RequirePage page="/approvals"><ApprovalQueue /></RequirePage>} />
        <Route path="/authorizations" element={<RequirePage page="/authorizations"><Authorizations /></RequirePage>} />
        <Route path="/masters" element={<RequirePage page="/masters"><Masters /></RequirePage>} />
        <Route path="/audit-trail" element={<RequirePage page="/audit-trail"><AuditTrail /></RequirePage>} />
        <Route path="/settings" element={<RequirePage page="/settings"><Settings /></RequirePage>} />
      </Routes>
      </Layout>
    </WhatsNewProvider>
  );
}
